// PublicBookingPage.js - Public-Facing Booking Page (No Authentication Required)

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, CircularProgress, Alert,
  Stack, TextField, Divider, Chip
} from '@mui/material';
import {
  EventAvailable as BookingIcon,
  AccessTime as DurationIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { bookingPageService } from './activityCommService';

const PublicBookingPage = () => {
  const { bookingUuid } = useParams();

  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState('date'); // 'date' | 'form' | 'confirmed'
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    custom_answers: {}
  });

  useEffect(() => {
    loadBookingPage();
  }, [bookingUuid]);

  const loadBookingPage = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await bookingPageService.getPublicPage(bookingUuid);
      const data = res.data || res;
      setPageData(data);
      document.title = `${data.title || 'Book a Meeting'} | Booking`;
    } catch (err) {
      setError('This booking page is not available. It may have been deactivated or the link is incorrect.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setSlotsLoading(true);
    setAvailableSlots([]);

    try {
      const res = await bookingPageService.getAvailableSlots(bookingUuid, { date });
      setAvailableSlots(res.data || res || []);
    } catch (err) {
      setError('Failed to load available time slots. Please try again.');
      console.error(err);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep('form');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomAnswer = (label, value) => {
    setFormData(prev => ({
      ...prev,
      custom_answers: { ...prev.custom_answers, [label]: value }
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    // Validate required custom questions
    const customQuestions = pageData?.custom_questions || [];
    for (const q of customQuestions) {
      if (q.required && !formData.custom_answers[q.label]?.trim()) {
        setError(`"${q.label}" is required`);
        return;
      }
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        slot_time: selectedSlot.time || selectedSlot.start_time || selectedSlot,
        date: selectedDate,
        custom_answers: formData.custom_answers
      };

      const res = await bookingPageService.createBooking(bookingUuid, payload);
      setBookingResult(res.data || res);
      setStep('confirmed');
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to create booking. The slot may no longer be available.';
      setError(message);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => {
    if (!pageData?.min_notice) return new Date().toISOString().split('T')[0];
    const minDate = new Date();
    minDate.setHours(minDate.getHours() + (pageData.min_notice || 0));
    return minDate.toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + (pageData?.max_days_ahead || 60));
    return maxDate.toISOString().split('T')[0];
  };

  const primaryColor = pageData?.primary_color || '#1976d2';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !pageData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <BookingIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight={600}>Booking Unavailable</Typography>
          <Typography color="text.secondary">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  // Confirmation Screen
  if (step === 'confirmed') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5', p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Booking Confirmed!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {pageData?.confirmation_message || 'Your booking has been confirmed. We look forward to meeting with you.'}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Stack spacing={1.5} sx={{ textAlign: 'left' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Event</Typography>
              <Typography variant="body2" fontWeight={600}>{pageData?.title}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Date</Typography>
              <Typography variant="body2" fontWeight={600}>
                {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Time</Typography>
              <Typography variant="body2" fontWeight={600}>
                {selectedSlot?.display || selectedSlot?.time || selectedSlot?.start_time || selectedSlot}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Duration</Typography>
              <Typography variant="body2" fontWeight={600}>{pageData?.duration || 30} minutes</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Name</Typography>
              <Typography variant="body2" fontWeight={600}>{formData.name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body2" fontWeight={600}>{formData.email}</Typography>
            </Box>
          </Stack>
          {bookingResult?.confirmation_id && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Confirmation ID: {bookingResult.confirmation_id}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4, px: 2 }}>
      <Box sx={{ maxWidth: 700, mx: 'auto' }}>
        {/* Header */}
        <Paper sx={{ p: 4, mb: 3, borderTop: `4px solid ${primaryColor}` }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: primaryColor }}>
            {pageData?.title || 'Book a Meeting'}
          </Typography>
          {pageData?.description && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {pageData.description}
            </Typography>
          )}
          <Stack direction="row" spacing={2}>
            <Chip
              icon={<DurationIcon />}
              label={`${pageData?.duration || 30} minutes`}
              variant="outlined"
            />
            {pageData?.host_name && (
              <Chip
                icon={<PersonIcon />}
                label={pageData.host_name}
                variant="outlined"
              />
            )}
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Date & Slot Selection */}
        {step === 'date' && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle', color: primaryColor }} />
              Select a Date & Time
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <TextField
              label="Select Date"
              type="date"
              fullWidth
              value={selectedDate}
              onChange={(e) => handleDateSelect(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getMinDate(), max: getMaxDate() }}
              sx={{ mb: 3 }}
            />

            {slotsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            )}

            {!slotsLoading && selectedDate && availableSlots.length === 0 && (
              <Alert severity="info">
                No available time slots for this date. Please select a different date.
              </Alert>
            )}

            {!slotsLoading && availableSlots.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Available Time Slots
                </Typography>
                <Grid container spacing={1}>
                  {availableSlots.map((slot, idx) => {
                    const slotDisplay = slot.display || slot.time || slot.start_time || slot;
                    const isSelected = selectedSlot === slot;
                    return (
                      <Grid item xs={4} sm={3} key={idx}>
                        <Button
                          fullWidth
                          variant={isSelected ? 'contained' : 'outlined'}
                          onClick={() => handleSlotSelect(slot)}
                          sx={{
                            py: 1.5,
                            borderColor: primaryColor,
                            color: isSelected ? '#fff' : primaryColor,
                            bgcolor: isSelected ? primaryColor : 'transparent',
                            '&:hover': {
                              bgcolor: isSelected ? primaryColor : `${primaryColor}10`,
                              borderColor: primaryColor
                            }
                          }}
                        >
                          {typeof slotDisplay === 'string' ? slotDisplay : JSON.stringify(slotDisplay)}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Paper>
        )}

        {/* Booking Form */}
        {step === 'form' && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle', color: primaryColor }} />
              Your Details
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Selected slot summary */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: `${primaryColor}08` }}>
              <Typography variant="body2" color="text.secondary">
                {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                {' at '}
                {selectedSlot?.display || selectedSlot?.time || selectedSlot?.start_time || selectedSlot}
                {' - '}
                {pageData?.duration || 30} minutes
              </Typography>
              <Button size="small" onClick={() => setStep('date')} sx={{ mt: 0.5, color: primaryColor }}>
                Change
              </Button>
            </Paper>

            <Stack spacing={2}>
              <TextField
                label="Name"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
              />
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
              />
              <TextField
                label="Phone (optional)"
                fullWidth
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
              />

              {/* Custom Questions */}
              {pageData?.custom_questions?.map((q, idx) => (
                <TextField
                  key={idx}
                  label={q.label + (q.required ? ' *' : '')}
                  fullWidth
                  required={q.required}
                  type={q.type === 'number' ? 'number' : q.type === 'email' ? 'email' : 'text'}
                  multiline={q.type === 'textarea'}
                  rows={q.type === 'textarea' ? 3 : 1}
                  value={formData.custom_answers[q.label] || ''}
                  onChange={(e) => handleCustomAnswer(q.label, e.target.value)}
                />
              ))}
            </Stack>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => setStep('date')}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting}
                sx={{ bgcolor: primaryColor, '&:hover': { bgcolor: primaryColor } }}
              >
                {submitting ? 'Booking...' : 'Confirm Booking'}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default PublicBookingPage;
